import React, { useState } from "react";
import { View, TouchableOpacity } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { AppText } from "@/components/AppText";
import { UserSubscriptionWithRefundProcess } from "@/types/userSubscription";
import { PhoneRefundFlowModal } from "../hotelReservation/PhoneRefundFlowModal";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { FontFamilies } from "@/constants/Typography";
import Icons from "@/assets/icons";
import { useTrackImpression, useTrackClick } from "@/hooks/tracking";

type Props = {
  subscription: UserSubscriptionWithRefundProcess;
};

const UserSubscriptionCard: React.FC<Props> = ({ subscription }) => {
  const [showRefundModal, setShowRefundModal] = useState(false);
  const priceDisplay = (subscription.usdPrice ?? 0).toFixed(2);

  // Track when card is shown
  useTrackImpression(TRACKING_EVENTS.SUBSCRIPTION_REFUND_SHOWN, {
    serviceName: subscription.serviceName,
    provider: subscription.provider,
    amount: subscription.usdPrice,
  });

  // Track when "Request refund" button is clicked
  const handleStartRefund = useTrackClick(
    TRACKING_EVENTS.SUBSCRIPTION_REFUND_CLICKED,
    {
      serviceName: subscription.serviceName,
      provider: subscription.provider,
      amount: subscription.usdPrice,
    }
  )(() => {
    setShowRefundModal(true);
  });

  const handleCloseModal = () => {
    setShowRefundModal(false);
  };

  return (
    <View style={styles.card}>
      {/* Top section with amount and title */}
      <View style={styles.topSection}>
        <View style={styles.leftContent}>
          <View style={styles.amountBadge}>
            <AppText style={styles.amountText}>${priceDisplay}</AppText>
          </View>
          <AppText style={styles.titleText}>
            Cancel subscription on {subscription.provider} for{" "}
            {subscription.serviceName}
          </AppText>
        </View>
        <View style={styles.subscriptionImageContainer}>
          <View style={styles.subscriptionLogo}>
            <AppText style={styles.subscriptionLogoText}>
              {subscription.serviceName.charAt(0).toUpperCase()}
            </AppText>
          </View>
        </View>
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Bottom buttons */}
      <View style={styles.buttonSection}>
        <View style={styles.statusContent}>
          <Icons.NoProofPaper width={16} height={16} />
          <AppText style={styles.statusText}>
            Cancel subscription on {subscription.provider}
          </AppText>
        </View>
        <TouchableOpacity
          style={styles.requestButton}
          onPress={handleStartRefund}
        >
          <AppText style={styles.requestButtonText}>Request refund</AppText>
        </TouchableOpacity>
      </View>

      <PhoneRefundFlowModal
        visible={showRefundModal}
        item={subscription}
        onClose={handleCloseModal}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#EBEBEB",
    overflow: "hidden",
  },
  topSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  leftContent: {
    flex: 1,
    padding: 16,
    gap: 10,
  },
  amountBadge: {
    backgroundColor: "#D2F2D7",
    paddingHorizontal: 12,
    borderRadius: 120,
    alignSelf: "flex-start",
    height: 30,
    justifyContent: "center",
    alignItems: "center",
  },
  amountText: {
    fontSize: 15,
    fontWeight: "600",
    color: "#161D14",
    textAlign: "center",
  },
  titleText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    color: "#161D14",
  },
  subscriptionImageContainer: {
    width: 120,
    alignSelf: "stretch",
    borderTopRightRadius: 8,
    backgroundColor: "#F7F8FA",
    justifyContent: "center",
    alignItems: "center",
  },
  subscriptionLogo: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "#E5E7EB",
    justifyContent: "center",
    alignItems: "center",
  },
  subscriptionLogoText: {
    fontSize: 24,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
  buttonSection: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    padding: 8,
    paddingHorizontal: 16,
  },
  statusContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: 1,
  },
  statusText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    fontWeight: "400",
    color: "#161D14",
    lineHeight: 13 * 1.5385,
    letterSpacing: -0.02 * 13,
  },
  requestButton: {
    backgroundColor: "#0F9129",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.12)",
    borderRadius: 100,
    paddingHorizontal: 20,
    height: 39,
    justifyContent: "center",
    alignItems: "center",
  },
  requestButtonText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 15,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

export default UserSubscriptionCard;
